import React from 'react';
import { PageSection, FeaturedPropertiesSectionContent } from '../../types/page';
import { PropertyGrid } from '../property/PropertyGrid';
import { Button } from '../ui/button';
import { useProperties } from '../../hooks/useProperties';
import { ArrowRight } from 'lucide-react';
import { SectionSkeleton } from '../ui/Skeleton';
import { PropertyGridSkeleton } from '../ui/Skeleton';

interface FeaturedPropertiesSectionProps {
  section: PageSection;
  className?: string;
}

export const FeaturedPropertiesSection: React.FC<FeaturedPropertiesSectionProps> = ({
  section,
  className = ''
}) => {
  const content = section.content as FeaturedPropertiesSectionContent;
  const { properties, loading, error } = useProperties();

  // Null check for content
  if (!content) {
    return (
      <section className={`py-20 bg-gray-50 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              {section.title}
            </h2>
            {section.subtitle && (
              <p className="text-xl text-gray-600 mb-8">{section.subtitle}</p>
            )}
            <p className="text-gray-600">Error: Configuración de sección no disponible</p>
          </div>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <SectionSkeleton title={section.title} subtitle={section.subtitle}>
        <PropertyGridSkeleton count={content?.limit || 6} />
      </SectionSkeleton>
    );
  }

  if (error) {
    return (
      <section className={`py-20 bg-gray-50 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-red-600">Error al cargar las propiedades</p>
          </div>
        </div>
      </section>
    );
  }

  // Filtrar propiedades según el criterio
  let filteredProperties = properties || [];

  switch (content?.filterBy) {
    case 'featured':
      filteredProperties = filteredProperties.filter(p => p.featured);
      break;
    case 'recent':
      filteredProperties = filteredProperties.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      break;
    case 'popular':
      filteredProperties = filteredProperties.sort((a, b) => (b.views || 0) - (a.views || 0));
      break;
    default:
      // Si no se especifica filtro, usar featured por defecto
      filteredProperties = filteredProperties.filter(p => p.featured);
  }

  // Limitar el número de propiedades
  const displayProperties = filteredProperties.slice(0, content?.limit || 6);

  if (displayProperties.length === 0) {
    return (
      <section className={`py-20 bg-gray-50 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              {section.title}
            </h2>
            {section.subtitle && (
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {section.subtitle}
              </p>
            )}
          </div>
          <div className="text-center">
            <p className="text-gray-600">No hay propiedades destacadas disponibles</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 bg-gray-50 ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            {section.title}
          </h2>
          {section.subtitle && (
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Properties Grid */}
        <PropertyGrid properties={displayProperties} />

        {/* View All Button */}
        {content?.showViewAll && filteredProperties.length > displayProperties.length && (
          <div className="text-center mt-12">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
              onClick={() => window.location.href = '/ventas'}
            >
              Ver Todas las Propiedades
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};